import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { OpsBoardColumn, OpsOverview, ProductLine } from '@popcart/core/v1';
import { Icon, type IconName } from '../ui/Icon';
import { LineDot, StatusPill, type Tone, EmptyState } from '../ui/bits';
import { paymentLabel, peso, count, minutes } from '../lib/format.js';
import { schematicPositions } from '../lib/board.js';
import { STATE_ART, salesBarClass } from '../lib/voice.js';
import { Fig } from '../ui/viz';
import type { OpsRider, OpsShop } from './queries';
import './riders/riders.css';

type Board = (OpsBoardColumn & { label: string })[];
type Ticket = OpsBoardColumn['orders'][number];

const PAY_HINT: Record<string, string> = { cod: 'Cash on delivery' };

/** One ticket: code and minutes on top, line dots and the total below; late ones wear red. */
function TicketCard({ o, stage }: { o: Ticket; stage: OpsBoardColumn['stage'] }) {
  const done = stage === 'delivered';
  const late = o.late && !done;
  const shops = o.shopNames.length > 1 ? o.shopNames.join(' + ') : o.shopNames[0] ?? '';
  return (
    <li className={late ? 'lb-t late' : 'lb-t'} aria-label={`${o.code}${o.late ? ', late' : ''}${o.speed === 'express' ? ', Express' : ''}`} title={[shops, o.addressShort].filter(Boolean).join(' · ')}>
      <div className="lb-r">
        <span className="lb-code">{o.code}</span>
        {done ? (
          <span className="lb-done" title="Delivered">
            <Icon name="check" size="sm" />
          </span>
        ) : late ? (
          <span className="dv-chip bad">{o.lateBy ? `+${o.lateBy}m` : 'Late'}</span>
        ) : (
          <span className="lb-min">{o.minutes}m</span>
        )}
      </div>
      <div className="lb-r">
        <span className="dv-lines" aria-label={o.lines.join(', ')}>
          {o.lines.map((l) => (
            <LineDot key={l} line={l} />
          ))}
          {o.speed === 'express' ? (
            <span className="lb-bolt" title="Express">
              <Icon name="bolt" size="sm" />
            </span>
          ) : null}
        </span>
        <Fig text={peso(o.totalCentavos)} size="xs" animate={false} />
      </div>
      <div className="lb-r lb-meta">
        <span className="lb-addr">{o.addressShort}</span>
        <span className={o.payment === 'cod' ? 'lb-pay cod' : 'lb-pay'} title={PAY_HINT[o.payment] ?? paymentLabel(o.payment)}>
          {paymentLabel(o.payment)}
        </span>
      </div>
      {o.shopNames.length > 1 ? (
        <span className="lb-shops">
          <Icon name="bag" size="sm" />
          {o.shopNames.length} shops
        </span>
      ) : null}
    </li>
  );
}

/** The five-column live board. `limit` trims each column (overview) with a link to the full page. */
export function LiveBoard({ board, limit }: { board: Board; limit?: number }) {
  return (
    <div className="lb">
      {board.map((col) => {
        const shown = limit ? col.orders.slice(0, limit) : col.orders;
        const more = col.orders.length - shown.length;
        const late = col.stage === 'delivered' ? 0 : col.orders.filter((o) => o.late).length;
        return (
          <section key={col.stage} className={col.count ? 'lb-col' : 'lb-col zero'} aria-labelledby={`col-${col.stage}`}>
            <div className="lb-head">
              <h3 id={`col-${col.stage}`}>{col.label}</h3>
              {late ? <span className="dv-chip bad">{late} late</span> : null}
              <span aria-label={`${col.count} orders`}>
                <Fig text={count(col.count)} size="sm" animate={false} />
              </span>
            </div>
            {shown.length ? (
              <ul className="lb-list">
                {shown.map((o) => (
                  <TicketCard key={o.orderId} o={o} stage={col.stage} />
                ))}
              </ul>
            ) : (
              <p className="lb-none">
                <span className="sr-only">Nothing here</span>
              </p>
            )}
            {more > 0 ? (
              <Link className="dv-link lb-more" to="/ops/live">
                +{more} more <Icon name="chevronRight" />
              </Link>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

/** Board filter pills: all shops / one shop / late only. */
export function BoardFilters({
  shopNames,
  shop,
  setShop,
  lateOnly,
  setLateOnly,
  late,
}: {
  shopNames: string[];
  shop: string | null;
  setShop(s: string | null): void;
  lateOnly: boolean;
  setLateOnly(v: boolean): void;
  late: number;
}) {
  return (
    <div className="lb-filters" role="group" aria-label="Filter orders">
      <button type="button" className="pill tab chip-outline lb-f" aria-pressed={shop === null} onClick={() => setShop(null)}>
        All shops
      </button>
      {shopNames.map((n) => (
        <button key={n} type="button" className="pill tab chip-outline lb-f" aria-pressed={shop === n} onClick={() => setShop(shop === n ? null : n)}>
          {n}
        </button>
      ))}
      <span className="grow" />
      <button type="button" className={late ? 'pill tab chip-outline lb-f lb-late has' : 'pill tab chip-outline lb-f lb-late'} aria-pressed={lateOnly} aria-label={`Late only · ${late}`} onClick={() => setLateOnly(!lateOnly)}>
        <Icon name="clock" size="sm" />
        Late
        <span className="lb-n">{late}</span>
      </button>
    </div>
  );
}

export function shopStatusView(s: OpsShop): { label: string; tone: Tone } {
  if (s.status === 'pending') return { label: 'Not live yet', tone: 'warning' };
  if (s.status === 'paused') return { label: 'Paused', tone: 'warning' };
  if (s.status === 'suspended') return { label: 'Suspended', tone: 'error' };
  if (!s.open) return { label: 'Closed', tone: 'neutral' };
  if (!s.acceptingOrders) return { label: 'Not taking orders', tone: 'warning' };
  return { label: 'Open', tone: 'success' };
}

function shopSub(s: OpsShop) {
  const kind = s.kind === 'first_party' ? 'FIRST-PARTY' : 'PARTNER';
  const dist = s.distanceKm !== null && s.distanceKm !== undefined ? ` · ${s.distanceKm} KM` : '';
  return s.status === 'pending' ? `NOT LIVE YET · ${kind}${dist}` : `${kind}${dist}`;
}

export function ShopsTable({ shops, actions, name }: { shops: OpsShop[]; actions?: (s: OpsShop) => ReactNode; name?: (s: OpsShop) => ReactNode }) {
  if (!shops.length) return <EmptyState art={STATE_ART.noResults} title="No shops yet">Approved shops show up here, ready for orders.</EmptyState>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th scope="col">Shop</th>
            <th scope="col">Sells</th>
            <th scope="col">Status</th>
            <th scope="col" className="num">Orders</th>
            <th scope="col" className="num">Sales</th>
            <th scope="col" className="num">Prep</th>
            <th scope="col" className="num">Rating</th>
            {actions ? <th scope="col" className="num">Actions</th> : <th scope="col" className="num">Payout due</th>}
          </tr>
        </thead>
        <tbody>
          {shops.map((s) => {
            const st = shopStatusView(s);
            const pending = s.status === 'pending';
            return (
              <tr key={s.id}>
                <th scope="row" style={{ textTransform: 'none', letterSpacing: 0, font: 'var(--pc-type-ui-label)', color: 'var(--pc-text-primary)' }}>
                  {name ? name(s) : <b>{s.name}</b>}
                  <div className="t-monocap muted">{shopSub(s)}</div>
                </th>
                <td>
                  <span className="row" aria-label={s.lines.join(', ')}>
                    {s.lines.map((l: ProductLine) => (
                      <LineDot key={l} line={l} />
                    ))}
                  </span>
                </td>
                <td>
                  <StatusPill tone={st.tone}>{st.label}</StatusPill>
                </td>
                <td className="num">{pending ? '—' : count(s.ordersToday)}</td>
                <td className="num">{pending ? '—' : peso(s.salesTodayCentavos)}</td>
                <td className="num">{pending ? '—' : minutes(s.prepMinutes)}</td>
                <td className="num">{s.rating === null || s.rating === undefined ? '—' : s.rating.toFixed(1)}</td>
                <td className="num">{actions ? <div className="actions">{actions(s)}</div> : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Riders schematic: positioned pins on decorative roads (no map tiles yet, R7 map styling open).
 * §10 RIDERS colours: a free rider is a Pop Yellow pin, a busy one Cart Blue with a Yellow centre,
 * both with an Ink edge. No riders: the §10 parked-helmet scene (BO-24).
 */
export function RidersMap({ riders, quietEmpty }: { riders: OpsRider[]; quietEmpty?: boolean }) {
  const placed = riders.filter((r) => r.online && r.position);
  const pts = schematicPositions(placed.map((r) => r.position as { lat: number; lng: number }));
  const busy = placed.filter((r) => r.activeDeliveries > 0).length;
  if (!placed.length) {
    if (quietEmpty)
      return (
        <div className="dv-quiet">
          <Icon name="pin" />
          Nobody on the map yet
        </div>
      );
    return (
      <EmptyState art={STATE_ART.noRiders} title="No riders online">
        Riders show up on the map when they go online in the rider app.
      </EmptyState>
    );
  }
  return (
    <>
      <div className="map" role="img" aria-label={`${placed.length} riders on the map, ${busy} on a delivery, ${placed.length - busy} free`}>
        <div className="road" style={{ left: 0, right: 0, top: '46%', height: 14 }} />
        <div className="road" style={{ top: 0, bottom: 0, left: '38%', width: 14 }} />
        <div className="road" style={{ top: 0, bottom: 0, left: '76%', width: 10 }} />
        <div className="road" style={{ left: 0, right: 0, top: '18%', height: 10 }} />
        {placed.map((r, i) => (
          <div key={r.userId} className={r.activeDeliveries > 0 ? 'pin rider busy' : 'pin rider'} style={{ left: `${pts[i]?.x ?? 50}%`, top: `${pts[i]?.y ?? 50}%` }} title={r.name} />
        ))}
      </div>
      <div className="row t-caption muted" style={{ gap: 'var(--pc-space-16)' }} aria-hidden="true">
        <span className="row">
          <span className="legend-dot" />
          Free
        </span>
        <span className="row">
          <span className="legend-dot busy" />
          On a delivery
        </span>
      </div>
    </>
  );
}

const ALERT_VIEW: Record<OpsOverview['alerts'][number]['kind'], { icon: IconName; tone: Tone }> = {
  late_order: { icon: 'clock', tone: 'error' },
  shop_application: { icon: 'bag', tone: 'warning' },
  weigh_over_tolerance: { icon: 'scale', tone: 'warning' },
  rider_cash_limit: { icon: 'cash', tone: 'info' },
  payment_failed: { icon: 'alert', tone: 'error' },
};

/** "Needs you": shop applications collapse into one card carrying the screen's one primary button. */
export function Alerts({ alerts }: { alerts: OpsOverview['alerts'] }) {
  if (!alerts.length) return <p className="muted">Nothing needs you right now.</p>;
  const apps = alerts.filter((a) => a.kind === 'shop_application');
  const rest = alerts.filter((a) => a.kind !== 'shop_application');
  return (
    <ul className="stack s12">
      {apps.length ? (
        <li className="alert">
          <span className="badge st-warning">
            <Icon name="bag" />
          </span>
          <div className="stack">
            <b>{apps.length === 1 ? '1 shop wants to join' : `${apps.length} shops want to join`}</b>
            <span className="t-caption">Check permits, location and photos before they go live.</span>
            <Link className="btn btn-primary" to="/ops/shops">
              Review applications
            </Link>
          </div>
        </li>
      ) : null}
      {rest.map((a, i) => {
        const v = ALERT_VIEW[a.kind] ?? { icon: 'alert', tone: 'info' };
        return (
          <li key={`${a.kind}-${a.ref ?? i}`} className="alert">
            <span className={`badge st-${v.tone}`}>
              <Icon name={v.icon} />
            </span>
            <div className="stack s4">
              <b>{a.title}</b>
              <span className="t-caption">{a.detail}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const LINE_NAME: Record<string, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery', express_surcharge: 'Express surcharge' };

export function SalesByLine({ rows }: { rows: OpsOverview['salesByLine'] }) {
  const total = rows.reduce((s, r) => s + Math.max(0, r.centavos), 0);
  if (!rows.length) return <p className="muted">No sales yet.</p>;
  return (
    <ul className="stack s12">
      {rows.map((r) => {
        const pct = total ? Math.round((Math.max(0, r.centavos) / total) * 100) : 0;
        return (
          <li key={r.line} className="stack s4">
            <div className="row between">
              <span className="row">
                {r.line === 'express_surcharge' ? <Icon name="bolt" size="sm" /> : <LineDot line={r.line} />}
                {LINE_NAME[r.line] ?? r.line}
              </span>
              <span className="t-mono">{peso(r.centavos)}</span>
            </div>
            <div className="bar sales" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${LINE_NAME[r.line] ?? r.line}: ${pct}% of sales`}>
              <i className={pct ? salesBarClass(r.line) : `${salesBarClass(r.line)} empty`} style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The dashboard ring (styles/dash.css .dv-ring) with a guarded first-frame fill, so it renders
 * where requestAnimationFrame doesn't exist (tests, server render). Local twin of viz `Ring`.
 */
export function GaugeRing({ pct, tone, size = 76, children }: { pct: number | null; tone: 'good' | 'warn' | 'bad'; size?: number; children?: ReactNode }) {
  const r = 32;
  const c = 2 * Math.PI * r;
  const [shown, setShown] = useState(typeof requestAnimationFrame === 'function' ? 0 : pct ?? 0);
  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') {
      setShown(pct ?? 0);
      return;
    }
    const t = requestAnimationFrame(() => setShown(pct ?? 0));
    return () => cancelAnimationFrame(t);
  }, [pct]);
  return (
    <span className="lb-ring" style={{ width: size, height: size }} aria-hidden="true">
      <svg className="dv-ring" viewBox="0 0 76 76" style={{ width: size, height: size }}>
        <circle className="trk" cx="38" cy="38" r={r} />
        {pct !== null ? <circle className={`val ${tone}`} cx="38" cy="38" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, Math.max(0, shown)) / 100)} /> : null}
      </svg>
      {children ? <span className="lb-ring-mid">{children}</span> : null}
    </span>
  );
}
